// Vendored from react-use, and simplified for our use-case
// https://www.npmjs.com/package/react-use
import {
	type Dispatch,
	type EffectCallback,
	type SetStateAction,
	useCallback,
	useEffect,
	useRef,
	useState,
} from "react";

const useEffectOnce = (effect: EffectCallback) => {
	// eslint-disable-next-line react-hooks/exhaustive-deps
	useEffect(effect, []);
};

const useUnmount = (fn: () => void): void => {
	const fnRef = useRef(fn);

	// update the ref each render so if it change the newest callback will be invoked
	fnRef.current = fn;

	useEffectOnce(() => () => fnRef.current());
};

const useRafState = <S>(initialState: S | (() => S)): [S, Dispatch<SetStateAction<S>>] => {
	const frame = useRef(0);
	const [state, setState] = useState(initialState);

	const setRafState = useCallback((value: S | ((prevState: S) => S)) => {
		cancelAnimationFrame(frame.current);

		frame.current = requestAnimationFrame(() => {
			setState(value);
		});
	}, []);

	useUnmount(() => {
		cancelAnimationFrame(frame.current);
	});

	return [state, setRafState];
};

// Define the type for options that can be passed to the hook
interface Options {
	onChange?: (width: number, height: number) => void; // Callback function to execute on window resize (optional)
}

const useWindowSize = ({ onChange }: Options = {}) => {
	// Use the useRafState hook to maintain the current window size (width and height)
	const [state, setState] = useRafState<{ width: number; height: number }>({
		width: window.innerWidth,
		height: window.innerHeight,
	});

	useEffect((): (() => void) | void => {
		const handler = () => {
			const width = window.innerWidth;
			const height = window.innerHeight;

			// Update the state with the new window size
			setState({
				width,
				height,
			});

			// If an onChange callback is provided, call it with the new dimensions
			if (onChange) onChange(width, height);
		};

		// Add event listener for the resize event
		window?.addEventListener?.("resize", handler);

		// Cleanup function to remove the event listener when the component is unmounted (it's for performance optimization)
		return () => {
			window.removeEventListener("resize", handler);
		};
	}, [onChange, setState]);

	// Return the current window size (width and height)
	return state;
};

export default useWindowSize;
